// owner-map: OP-11.spa
import { fetchSeries, resolveSeriesName } from "../services/signalsApi.js";
import { useState, useCallback } from "react";
import { fuzzyMatch } from "../lib/series/fuzzyMatch.js";
import { classifySeriesResponse, networkFailureVerdict } from "../lib/series/seriesAvailability.js";
import { addSeriesData } from "../lib/colors.js";
import { selectBranchRuns } from "../lib/forecast/selectors.js";
import { estimateSeriesImpacts } from "../lib/forecast/forecastClient.js";
import { seriesImpactsSet } from "../lib/forecast/reducer.js";
import { getRunState, runStore } from "../store/runStore.js";

/**
 * Series-panel domain: the watchlist `series` state plus the fetch/verify/add/
 * remove functions that drive it. Originally moved verbatim from AppInner. Bound to the
 * run-store singleton (`getRunState` / `runStore.dispatch`): no store handles are threaded in.
 *
 * A series entry additionally carries `unavailable` / `unavailableReason` /
 * `unavailableTransient` when the server can't currently serve it — set by
 * `fetchLiveSeries` and cleared as soon as data arrives. That distinction matters
 * on the reload path: a signal saved to the library when it WAS available can stop
 * resolving later, and without the flag its chip renders as plotted while drawing
 * nothing. See `lib/series/seriesAvailability.js`.
 *
 * @param {{ active: object|null }} args
 * @returns {{ series: object, setSeries: Function, fetchLiveSeries: Function, verifySeries: Function, addSeries: Function, removeSeries: Function }}
 */
export function useSeriesPanel({ active }) {
  const [series, setSeries] = useState({});
  // Flag a series the server can't currently serve, so the drawer can say so instead
  // of showing it as a normal plotted signal that silently draws nothing. Only ever
  // annotates a series already in state (never resurrects a removed one).
  const markUnavailable = useCallback((name, verdict) => {
    setSeries((s) => {
      if (!s[name]) return s;
      return {
        ...s,
        [name]: {
          ...s[name],
          unavailable: true,
          unavailableReason: verdict.reason,
          unavailableTransient: verdict.transient,
        },
      };
    });
  }, []);
  // Fetch live data + server-generated projection (logistic flattening + confidence bands)
  const fetchLiveSeries = useCallback(
    (name, staticSeries, resDate, ssid = null) => {
      // ssid: fetch a specific signals-service signal by id (a clicked suggestion,
      // whose corpus name can't round-trip through the name→id derivation).
      fetchSeries(name, { resDate, ssid })
        // Keep ok/status, not just the body: a 404 body parses fine as JSON, so
        // reading only the body made an UNAVAILABLE series indistinguishable from a
        // sparse-but-valid one (see lib/series/seriesAvailability.js).
        .then(async (r) => ({
          ok: r.ok,
          status: r.status,
          body: await r.json().catch((err) => {
            console.warn("series.body_parse_failed", r.status, err);
            return null;
          }),
        }))
        .then((res) => {
          const verdict = classifySeriesResponse(res);
          if (verdict.kind === "unavailable") {
            // A signal saved when it WAS available (a persisted library chip) can
            // stop resolving later. Degrade visibly rather than pretending it plotted.
            markUnavailable(name, verdict);
            return;
          }
          if (verdict.kind === "sparse") {
            // Keep the chip visible even when the server returns no points (e.g. an
            // FRED series with sparse data). The chart line just won't draw, but the
            // user's selection survives so they can manually remove it.
            return;
          }
          const data = res.body;
          const liveData = data.points.map((p) => ({
            date: new Date(p.date),
            value: p.value,
            past: true,
          }));
          // Projection with confidence bands from server
          const projData = (data.projection || []).map((p) => ({
            date: new Date(p.date),
            value: p.value,
            upper: p.upper,
            lower: p.lower,
            past: false,
          }));
          const merged = [...liveData, ...projData];
          const allVals = [
            ...merged.map((d) => d.value),
            ...projData.map((d) => d.upper).filter(Boolean),
            ...projData.map((d) => d.lower).filter(Boolean),
          ];
          if (!allVals.length) return;
          const yMin = Math.min(...allVals) * 0.95;
          const yMax = Math.max(...allVals) * 1.05;
          const unit = data.unit || staticSeries.unit || "";
          setSeries((s) => {
            if (!s[name]) return s;
            return {
              ...s,
              [name]: {
                ...s[name],
                data: merged,
                yMin: Math.round(yMin * 100) / 100,
                yMax: Math.round(yMax * 100) / 100,
                unit,
                sourceUrl: data.sourceUrl || null,
                // Data arrived — clear any prior unavailable mark so a recovered
                // signal (transient outage, or one that came back) stops being
                // flagged without needing a reload.
                unavailable: false,
                unavailableReason: null,
                unavailableTransient: false,
              },
            };
          });
        })
        .catch((_e) => {
          // Network error — we learned nothing about the signal itself, so flag it as
          // a transient load failure rather than silently leaving a blank chip.
          markUnavailable(name, networkFailureVerdict());
        });
    },
    [markUnavailable],
  );
  // Validate a user-typed ticker/index before adding to the drawer. Checks: (1) min
  // points (≥30); (2) recency (last point within 5y); (3) projection exists; (4) Yahoo
  // ticker fallback (handled server-side); (5) fuzzy suggestions on hard failure pulled
  // from the server's 404 `available` list. Returns {ok, name, error, suggestions}.
  const verifySeries = useCallback(async (q) => {
    const MIN_POINTS = 30;
    const MAX_AGE_DAYS = 5 * 365;
    try {
      const rs = await resolveSeriesName(q);
      const rd = await rs.json().catch(() => ({}));
      const name = rd.resolved || q;
      const dr = await fetchSeries(name);
      if (!dr.ok) {
        const body = await dr.json().catch(() => ({}));
        const available = Array.isArray(body.available) ? body.available : [];
        return {
          ok: false,
          name,
          error: `Couldn't find data for "${q}".`,
          suggestions: fuzzyMatch(q, available, 3),
        };
      }
      const data = await dr.json();
      const pts = Array.isArray(data.points) ? data.points : [];
      if (pts.length < MIN_POINTS)
        return {
          ok: false,
          name,
          error: `"${name}" has only ${pts.length} data point${pts.length === 1 ? "" : "s"} — too sparse to plot.`,
          suggestions: [],
        };
      const lastDate = new Date(pts[pts.length - 1].date);
      const ageDays = (Date.now() - lastDate.getTime()) / 86400000;
      if (ageDays > MAX_AGE_DAYS)
        return {
          ok: false,
          name,
          error: `"${name}" data is stale — last updated ${pts[pts.length - 1].date}.`,
          suggestions: [],
        };
      const proj = Array.isArray(data.projection) ? data.projection : [];
      if (proj.length === 0)
        return {
          ok: false,
          name,
          error: `"${name}" has no projection — too sparse to forecast forward.`,
          suggestions: [],
        };
      return { ok: true, name, error: null, suggestions: [] };
    } catch (err) {
      console.error("[Sayer] signal check failed:", q, err);
      return {
        ok: false,
        name: q,
        error: `Couldn't check "${q}" just now. Try adding it again in a minute.`,
        suggestions: [],
      };
    }
  }, []);
  const addSeries = useCallback(
    async (n, c, isAuto = false, ssid = null) => {
      const rd = active?.contract?.resolution_date || null;
      // The forecast this signal is added to, fixed before the awaits below: the user may
      // select another forecast while the name resolves, and the branch estimates must still
      // be this one's.
      const rootId = active?.id ?? null;
      // Resolve natural language to a known series name (Dow Jones, Brent, etc.).
      // Skip it for a clicked suggestion (ssid present): we already have the name,
      // and the ssid is fetched directly — the corpus name wouldn't resolve anyway.
      let resolvedName = n;
      if (!ssid) {
        try {
          const resp = await resolveSeriesName(n);
          const data = await resp.json();
          if (data.resolved) resolvedName = data.resolved;
        } catch (_e) {
          /* resolve unavailable, fall through */
        }
      }
      const r = addSeriesData(resolvedName, c);
      if (!r) return;
      setSeries((s) => {
        // Re-adding an existing series promotes it to a manual pick (so a later add
        // can't silently evict it) and keeps it visible. Keep whichever ssid we now
        // have: a re-add via a suggestion supplies one, a plain re-click doesn't and
        // must not erase it.
        if (s[resolvedName])
          return {
            ...s,
            [resolvedName]: {
              ...s[resolvedName],
              _auto: false,
              ssid: ssid || s[resolvedName].ssid,
            },
          };
        const keys = Object.keys(s);
        const next = { ...s };
        if (keys.length >= 2) {
          // Cap is 2 (two-axis chart). Evict an AUTO-populated series first so a
          // manual click never bumps another manual pick — only fall back to FIFO
          // (oldest) when both slots are manual.
          const victim = keys.find((k) => s[k]?._auto) ?? keys[0];
          delete next[victim];
        }
        // Carry the ssid on the entry so it reaches the per-forecast saved blob
        // (contentSave slims to {color, ssid?}) — without it a reloaded forecast
        // re-fetches by name only and 404s on every signals-service signal.
        next[resolvedName] = { ...r, _auto: isAuto, ssid: ssid || null };
        return next;
      });
      fetchLiveSeries(resolvedName, r, rd, ssid);
      // If branches already exist, fire one impact-estimation per branch for the new
      // index so its branch-dashed lines render against this newly-selected series too.
      // Branch RUN objects from the LIVE store, by the root id captured above (a branch
      // created while the name resolved is included; a render-time snapshot would miss it).
      // modification/forkDate/seriesImpacts map 1:1; id → runId.
      const branches = selectBranchRuns(getRunState(), rootId);
      if (branches.length > 0) {
        branches.forEach((br) => {
          estimateSeriesImpacts(br.modification, br.forkDate, [resolvedName]).then((impacts) => {
            if (!impacts || !impacts[resolvedName]) return;
            // dual-write new-index series impacts to store for this branch
            runStore.dispatch(
              seriesImpactsSet(br.runId, {
                ...(br.seriesImpacts || {}),
                [resolvedName]: impacts[resolvedName],
              }),
            );
          });
        });
      }
    },
    [active, fetchLiveSeries],
  );
  const removeSeries = useCallback(
    (n) =>
      setSeries((s) => {
        const o = { ...s };
        delete o[n];
        return o;
      }),
    [],
  );

  return {
    series,
    setSeries,
    fetchLiveSeries,
    verifySeries,
    addSeries,
    removeSeries,
  };
}
